import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { buildFreezerMap, firstRackToShow, VAK_STATUS } from "functions/freezerMap";

const { Text } = Typography;

// Colours per state: the worst basket decides a stelling's colour
const STYLE = {
  full: { fill: "#f6ffed", stroke: "#95de64", text: "#237804" },
  partial: { fill: "#fafafa", stroke: "#d9d9d9", text: "#262626" },
  [VAK_STATUS.FILLED]: { fill: "#f6ffed", stroke: "#95de64", text: "#237804" },
  [VAK_STATUS.EMPTY]: { fill: "#ffffff", stroke: "#d9d9d9", text: "#8c8c8c", dashed: true },
  [VAK_STATUS.LABEL]: { fill: "#fffbe6", stroke: "#ffd666", text: "#ad6800" },
  [VAK_STATUS.MIXED]: { fill: "#fff1f0", stroke: "#ffa39e", text: "#a8071a" },
};

const ROW_WIDTH = 104;
const AISLE_WIDTH = 96;
const TOP = 46;
const PLOT = 300;
const GAP = 4;

/**
 * The freezer from above (Vakken en looproute → Kaart): a block per stelling in the U, coloured by
 * what its baskets hold, and the chosen stelling's baskets as you stand in front of it. A basket
 * opens its vak in the table, where everything can be done to it. Drawn from functions/freezerMap.
 */
const FreezerMap = ({ entries, onShowVak }) => {
  const { t } = useTranslation();
  const map = useMemo(() => buildFreezerMap(entries), [entries]);
  const [selected, setSelected] = useState(null);

  // The first stelling with something to put right, until one is chosen; a chosen one that is gone falls back
  const allRacks = map.columns.filter((column) => column.kind === "row").flatMap((column) => column.racks);
  const current = allRacks.find((rack) => rack.stelling === selected) || allRacks.find((rack) => rack.stelling === firstRackToShow(map)) || null;
  useEffect(() => {
    if (!selected && current) setSelected(current.stelling);
  }, [selected, current]);

  if (allRacks.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("pick_route.map_empty")} />;

  let x = 8;
  const placed = map.columns.map((column) => {
    const width = column.kind === "row" ? ROW_WIDTH : AISLE_WIDTH;
    const at = { ...column, x, width };
    x += width;
    return at;
  });
  const width = x + 8;
  const aisles = placed.filter((column) => column.kind === "aisle");
  const turn = TOP - 10;

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${TOP + PLOT + 30}`} width="100%" style={{ maxWidth: 760, display: "block" }} role="img" aria-label={t("pick_route.map_title")}>
        <defs>
          <marker id="freezer-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M2 1L8 5L2 9" fill="none" stroke="#8c8c8c" strokeWidth="1.5" />
          </marker>
        </defs>
        {aisles.length === 2 && (
          <path
            d={`M${aisles[0].x + AISLE_WIDTH / 2} ${TOP + PLOT} L${aisles[0].x + AISLE_WIDTH / 2} ${turn} L${aisles[1].x + AISLE_WIDTH / 2} ${turn} L${aisles[1].x + AISLE_WIDTH / 2} ${TOP + PLOT}`}
            fill="none"
            stroke="#bfbfbf"
            strokeDasharray="5 5"
            markerEnd="url(#freezer-arrow)"
          />
        )}
        {placed.map((column) => {
          const center = column.x + column.width / 2;
          if (column.kind === "aisle") {
            return (
              <text key={column.aisle} x={center} y={TOP + PLOT + 22} textAnchor="middle" fontSize="12" fill="#8c8c8c">
                {column.aisle}
              </text>
            );
          }
          const count = column.racks.length;
          const blockHeight = Math.min(30, (PLOT - (count - 1) * GAP) / Math.max(1, count));
          const startY = TOP + PLOT - (count * blockHeight + (count - 1) * GAP);
          return (
            <g key={column.row}>
              <text x={center} y={14} textAnchor="middle" fontSize="13" fontWeight="600" fill="#262626">
                {column.title}
              </text>
              <text x={center} y={28} textAnchor="middle" fontSize="11" fill="#8c8c8c">
                {column.subtitle}
              </text>
              {column.racks.map((rack, index) => {
                const style = STYLE[rack.status];
                const y = startY + index * (blockHeight + GAP);
                const chosen = !!current && rack.stelling === current.stelling;
                const choose = () => setSelected(rack.stelling);
                return (
                  <g
                    key={rack.stelling}
                    data-stelling={rack.stelling}
                    role="button"
                    tabIndex={0}
                    aria-label={t("pick_route.map_rack", { stelling: rack.stelling })}
                    style={{ cursor: "pointer" }}
                    onClick={choose}
                    onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && choose()}
                  >
                    <rect
                      x={column.x + 6}
                      y={y}
                      width={column.width - 12}
                      height={blockHeight}
                      rx="4"
                      fill={style.fill}
                      stroke={chosen ? "#1677ff" : style.stroke}
                      strokeWidth={chosen ? 2 : 1}
                      strokeDasharray={style.dashed && !chosen ? "4 3" : undefined}
                    />
                    <text x={column.x + 14} y={y + blockHeight / 2 + 4} fontSize={blockHeight < 20 ? 11 : 13} fontWeight="600" fontFamily="monospace" fill={style.text}>
                      {rack.stelling}
                    </text>
                    <text x={column.x + column.width - 14} y={y + blockHeight / 2 + 4} textAnchor="end" fontSize="11" fill={style.text}>
                      {rack.counts.filled}/{rack.slots.length}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      <Space wrap size={[8, 4]} style={{ margin: "8px 0 16px" }}>
        <Text type="secondary">{t("pick_route.map_legend_hint")}</Text>
        <Tag color="success">{t("pick_route.map_status_full")}</Tag>
        <Tag>{t("pick_route.map_status_partial")}</Tag>
        <Tag color="warning">{t("pick_route.map_status_label")}</Tag>
        <Tag color="error">{t("pick_route.map_status_mixed")}</Tag>
      </Space>

      {!!current && <RackFront rack={current} column={placed.find((column) => column.row === current.row)} onShowVak={onShowVak} />}

      {(map.outside.length > 0 || map.withoutCode > 0) && (
        <div style={{ marginTop: 12 }}>
          {map.outside.length > 0 && (
            <div>
              <Text type="secondary">
                {t("pick_route.map_outside", { count: map.outside.length, codes: map.outside.map((entry) => entry.location).join(", ") })}
              </Text>
            </div>
          )}
          {map.withoutCode > 0 && (
            <div>
              <Text type="secondary">{t("pick_route.map_without_code", { count: map.withoutCode })}</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** One stelling as you stand in front of it: two baskets a row, A B at the top (on the floor: A B at the front). */
const RackFront = ({ rack, column, onShowVak }) => {
  const { t } = useTranslation();
  return (
    <Card
      size="small"
      data-rack-front={rack.stelling}
      title={t("pick_route.map_front_title", { stelling: rack.stelling, row: column.title, zone: column.subtitle })}
      extra={<Text type="secondary">{t(column.letters === "front-back" ? "pick_route.map_front_floor" : "pick_route.map_front_baskets")}</Text>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        {rack.slots.map(({ letter, vak }) => {
          const style = STYLE[vak ? vak.status : VAK_STATUS.EMPTY];
          const code = `${rack.stelling}${letter}`;
          return (
            <div
              key={letter}
              data-vak-cell={code}
              role={vak ? "button" : undefined}
              tabIndex={vak ? 0 : undefined}
              onClick={vak ? () => onShowVak(vak.code) : undefined}
              onKeyDown={vak ? (event) => event.key === "Enter" && onShowVak(vak.code) : undefined}
              style={{
                background: style.fill,
                border: `1px ${style.dashed ? "dashed" : "solid"} ${style.stroke}`,
                borderRadius: 6,
                padding: "8px 10px",
                minHeight: 58,
                cursor: vak ? "pointer" : "default",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontFamily: "monospace", fontWeight: 700, color: style.text }}>{code}</span>
                {!!vak && vak.shops.length > 0 && <span style={{ fontSize: 11, color: "#8c8c8c" }}>{vak.shops.join(" + ")}</span>}
              </div>
              <div style={{ color: vak && vak.status !== VAK_STATUS.EMPTY ? "#262626" : "#8c8c8c" }}>
                {!vak ? t("pick_route.map_no_vak") : vak.status === VAK_STATUS.EMPTY ? t("pick_route.map_vak_empty") : vak.article || vak.label}
              </div>
              {!!vak && vak.status === VAK_STATUS.LABEL && (
                <div style={{ fontSize: 12, color: style.text }}>{t("pick_route.map_vak_label", { label: vak.label })}</div>
              )}
              {!!vak && vak.status === VAK_STATUS.MIXED && <div style={{ fontSize: 12, color: style.text }}>{t("pick_route.map_vak_mixed")}</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default FreezerMap;
